import { chainCommands } from "prosemirror-commands";
import { keymap } from "prosemirror-keymap";
import type { Command, Plugin } from "prosemirror-state";
import {
  backspaceAtListItemStart,
  backspaceInEmptyListItem,
  clearFormatting,
  indentListItem,
  outdentListItem,
  setMarkFromFirstSegment,
  splitBlockKeepAttrs,
  splitOrExitListItem,
} from "./commands";
import { textSchema } from "./schema";
import { deletePill } from "./variables";

const insertHardBreak: Command = (state, dispatch) => {
  if (dispatch) {
    dispatch(
      state.tr
        .replaceSelectionWith(textSchema.nodes.hard_break.create())
        .scrollIntoView(),
    );
  }
  return true;
};

/** Tab outside a list must not move focus out of the editor. */
const swallow: Command = () => true;

/**
 * Editor-specific bindings, registered BEFORE keymap(baseKeymap) so list
 * handling wins inside lists and everything else falls through to the
 * ProseMirror defaults.
 *
 * Mod-z / Mod-y are intentionally NOT bound: undo is Redux-only. The global
 * use-shortcuts handler owns Cmd+Z (and preventDefaults it, which also
 * suppresses native contenteditable undo).
 */
export const buildKeymap = (): Plugin =>
  keymap({
    "Mod-b": setMarkFromFirstSegment(textSchema.marks.strong),
    "Mod-i": setMarkFromFirstSegment(textSchema.marks.em),
    "Mod-u": setMarkFromFirstSegment(textSchema.marks.underline),
    "Mod-Shift-x": setMarkFromFirstSegment(textSchema.marks.strike),
    // Spec §9 — clear formatting.
    "Mod-\\": clearFormatting,
    // Spec §4.5 — lists.
    Enter: chainCommands(splitOrExitListItem, splitBlockKeepAttrs),
    "Shift-Enter": insertHardBreak,
    Tab: chainCommands(indentListItem, swallow),
    "Shift-Tab": chainCommands(outdentListItem, swallow),
    Backspace: chainCommands(
      backspaceInEmptyListItem,
      backspaceAtListItemStart,
      deletePill(-1),
    ),
    Delete: deletePill(1),
  });
